import { usePrivy, useLogout } from '@privy-io/react-auth';
import { Link } from 'react-router-dom';
import { useConfig, useSwitchChain, useChainId } from 'wagmi';
import { Button, buttonVariants } from '@/components/ui/button';
import { useEvmWallet } from '@/hooks/useEvmWallet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import BoringAvatar from 'boring-avatars';
import { ChevronDown, TriangleAlert, Star } from 'lucide-react';
import { useGetUserKaiTokenBalance } from '@/hooks/useGetUserKaiTokenBalance';
import { useGetUserPoints } from '@/hooks/useGetUserPoints';
import { TOKENS, addTokenToWallet, canRegisterToken } from '@/tokens';
import { formatKai } from '@/helper';

// Avatar colors matching Shinkai brand
const AVATAR_COLORS = ['hsl(345, 87%, 65%)', 'hsl(232, 15%, 15%)'];

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

type WalletButtonProps = {
  showChain?: boolean;
};

export default function WalletButton({ showChain = true }: WalletButtonProps) {
  const { login, authenticated, ready, user } = usePrivy();
  const { logout } = useLogout();

  const { address, isConnected } = useEvmWallet();

  const { chains } = useConfig();
  const { switchChain, isPending: isSwitchingChain } = useSwitchChain();
  const chainId = useChainId();

  const { data: kaiBalance } = useGetUserKaiTokenBalance();
  const { data: userPoints } = useGetUserPoints();

  const handleDisconnect = () => {
    logout();
  };

  const targetChain = chains[0];
  const usingSupportedNetwork = chains.some((c) => c.id === chainId);
  const isUnsupportedNetwork = isConnected && !usingSupportedNetwork;

  const handleSwitchNetwork = () => {
    if (targetChain && switchChain) {
      switchChain({ chainId: targetChain.id });
    }
  };

  if (!ready) {
    return (
      <Button disabled className="h-9 gap-2 px-3" variant="outline">
        <div className="size-5 animate-pulse rounded-full bg-text-secondary/30" />
        <span className="hidden animate-pulse rounded-md bg-text-secondary/30 text-transparent sm:inline">
          Loading...
        </span>
      </Button>
    );
  }

  if (!authenticated || !isConnected) {
    return (
      <Button onClick={login} className="h-9 px-4">
        Connect
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Points Display */}
      {userPoints !== undefined && userPoints > 0 && (
        <Link
          to="/quests"
          className={cn(
            'hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-lg',
            'bg-brand/10 border border-brand/20',
            'transition-colors hover:bg-brand/20',
          )}
        >
          <Star className="h-4 w-4 text-brand fill-brand" />
          <span className="font-medium text-sm text-brand">
            {userPoints.toLocaleString()}
          </span>
        </Link>
      )}

      {/* KAI Balance Display */}
      {kaiBalance !== undefined && (
        <Button
          variant="outline"
          type="button"
          onClick={() => {
            if (canRegisterToken()) {
              addTokenToWallet(TOKENS.KAI);
            }
          }}
          className={cn('hidden sm:flex')}
        >
          <img src={TOKENS.KAI.image} alt="KAI" className="h-4 w-4" />
          <span className="font-medium text-sm text-text-default">
            {formatKai(kaiBalance, true)}
            <span className="ml-1.5 text-xs text-text-tertiary uppercase tracking-wider font-medium">
              KAI
            </span>
          </span>
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'h-9 gap-2 px-2 data-[state=open]:bg-bg-secondary',
          )}
        >
          <span className="relative inline-flex">
            <BoringAvatar
              size={20}
              name={address || 'wallet'}
              variant="pixel"
              colors={AVATAR_COLORS}
            />
            {isUnsupportedNetwork && (
              <span className="absolute -top-1 -right-1 inline-flex">
                <TriangleAlert
                  aria-hidden="true"
                  className="h-3 w-3 text-yellow-500"
                />
                <span className="sr-only">
                  Unsupported network. Switch to {targetChain?.name}.
                </span>
              </span>
            )}
          </span>
          <span className="hidden truncate sm:inline text-sm">
            {address ? truncateAddress(address) : 'Connected'}
          </span>
          <ChevronDown className="h-4 w-4 text-text-secondary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-bg-dark p-1">
          {/* Unsupported Network Warning */}
          {isUnsupportedNetwork && targetChain && (
            <>
              <div className="p-3">
                <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
                  <div className="flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-text-default">
                        Unsupported network
                      </p>
                      <p className="mt-1 text-text-secondary text-xs">
                        Switch to {targetChain.name} to continue.
                      </p>
                      <Button
                        className="mt-3 w-full"
                        disabled={isSwitchingChain}
                        onClick={handleSwitchNetwork}
                        size="xs"
                        type="button"
                        variant="outline"
                      >
                        {isSwitchingChain ? 'Switching...' : 'Switch Network'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-divider" />
            </>
          )}

          {/* Connected Network Indicator */}
          {!isUnsupportedNetwork && showChain && targetChain && (
            <>
              <div className="px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider font-medium">
                    Network
                  </span>
                  <div className="flex items-center gap-1.5 rounded-full bg-bg-tertiary px-2 py-0.5">
                    <div className="size-1.5 rounded-full bg-success animate-pulse" />
                    <span className="font-medium text-[10px] text-text-secondary">
                      {targetChain.name}
                    </span>
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-divider" />
            </>
          )}

          {/* Points (mobile fallback) */}
          {userPoints !== undefined && userPoints > 0 && (
            <>
              <div className="sm:hidden px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider font-medium">
                    Points
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Star className="h-4 w-4 text-brand fill-brand" />
                    <span className="font-medium text-sm text-brand">
                      {userPoints.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator className="sm:hidden bg-divider" />
            </>
          )}

          {/* KAI Token Balance (mobile fallback) */}
          <div className="sm:hidden px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-tertiary uppercase tracking-wider font-medium">
                Balance
              </span>
              <div className="flex items-center gap-1.5">
                <img src={TOKENS.KAI.image} alt="KAI" className="h-4 w-4" />
                <span className="font-medium text-sm text-text-default">
                  {formatKai(kaiBalance, true)}
                </span>
              </div>
            </div>
          </div>
          <DropdownMenuSeparator className="sm:hidden bg-divider" />

          <DropdownMenuItem
            asChild
            className="cursor-pointer focus:bg-bg-tertiary"
          >
            <Link to="/profile" className="w-full text-text-default">
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            asChild
            className="cursor-pointer focus:bg-bg-tertiary"
          >
            <Link to="/quests" className="w-full text-text-default">
              Challenges
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-divider" />
          <DropdownMenuItem
            onClick={handleDisconnect}
            className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            Disconnect
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
